export interface NestedPathSegment {
  type: 'program' | 'course' | 'card';
  uri: string;
}

export interface ParsedNestedPath {
  segments: NestedPathSegment[];
  currentEntity: NestedPathSegment | null;
  parentPath: string;
  fullPath: string;
  depth: number;
}

export class NestedPathHelper {
  static parsePath(url: string): ParsedNestedPath | null {
    if (!url) {
      return null;
    }

    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    const [mainPath] = cleanUrl.split('?');

    const pathSegments = mainPath.split('/').filter((segment) => segment.length > 0);

    if (pathSegments.length < 2) {
      return null;
    }

    const segments: NestedPathSegment[] = [];
    let currentEntity: NestedPathSegment | null = null;
    let i = 0;
    const tabKeywords = ['edit', 'content', 'collaborators', 'analytics', 'participants', 'publish-settings'];

    while (i < pathSegments.length) {
      const segment = pathSegments[i];

      if (segment === 'programs' && i + 1 < pathSegments.length) {
        const programUri = pathSegments[i + 1];
        segments.push({ type: 'program', uri: programUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'program', uri: programUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'programs') {
          continue;
        } else if (nextSegment === 'courses') {
          continue;
        } else if (nextSegment === 'cards') {
          currentEntity = { type: 'program', uri: programUri };
          continue;
        } else if (tabKeywords.includes(nextSegment)) {
          currentEntity = { type: 'program', uri: programUri };
          break;
        } else {
          break;
        }
      } else if (segment === 'courses' && i + 1 < pathSegments.length) {
        const courseUri = pathSegments[i + 1];
        segments.push({ type: 'course', uri: courseUri });
        i += 2;
        if (i >= pathSegments.length) {
          currentEntity = { type: 'course', uri: courseUri };
          break;
        }
        const nextSegment = pathSegments[i];
        if (nextSegment === 'programs') {
          break;
        } else if (nextSegment === 'cards') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (nextSegment === 'courses') {
          currentEntity = { type: 'course', uri: courseUri };
          continue;
        } else if (tabKeywords.includes(nextSegment)) {
          currentEntity = { type: 'course', uri: courseUri };
          break;
        } else {
          break;
        }
      } else if (segment === 'cards' && i + 1 < pathSegments.length) {
        const cardUri = pathSegments[i + 1];
        segments.push({ type: 'card', uri: cardUri });
        currentEntity = { type: 'card', uri: cardUri };
        // Card is always the leaf entity; the next segment (if any) is a tab like 'content' or 'edit'
        i += 2;
        break;
      } else if (tabKeywords.includes(segment)) {
        break;
      } else {
        break;
      }
    }

    if (segments.length === 0) {
      return null;
    }

    const fullPath = this.buildFullPath(segments);
    const parentPath = segments.length > 1 ? this.buildFullPath(segments.slice(0, -1)) : '';

    return {
      segments,
      currentEntity: currentEntity || segments[segments.length - 1],
      parentPath,
      fullPath,
      depth: segments.length,
    };
  }

  static buildFullPath(segments: NestedPathSegment[]): string {
    if (segments.length === 0) {
      return '';
    }

    const pathParts: string[] = [];
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;

      if (segment.type === 'program') {
        pathParts.push('programs');
        pathParts.push(segment.uri);
      } else if (segment.type === 'course') {
        pathParts.push('courses');
        pathParts.push(segment.uri);
      } else if (segment.type === 'card') {
        // Card is always a leaf entity; always use /cards/:cardUri
        pathParts.push('cards');
        pathParts.push(segment.uri);
      }
    }

    return '/' + pathParts.join('/');
  }

  static buildPathForEntity(
    entityType: 'program' | 'course' | 'card',
    entityUri: string,
    parentSegments: NestedPathSegment[] = [],
  ): string {
    const newSegment: NestedPathSegment = { type: entityType, uri: entityUri };
    const allSegments = [...parentSegments, newSegment];
    return this.buildFullPath(allSegments);
  }

  static getParentPath(url: string): string {
    const parsed = this.parsePath(url);
    return parsed?.parentPath || '';
  }

  static getCurrentEntity(url: string): NestedPathSegment | null {
    const parsed = this.parsePath(url);
    return parsed?.currentEntity || null;
  }

  static getAllSegments(url: string): NestedPathSegment[] {
    const parsed = this.parsePath(url);
    return parsed?.segments || [];
  }

  static getParentSegments(url: string): NestedPathSegment[] {
    const parsed = this.parsePath(url);
    if (!parsed || parsed.segments.length <= 1) {
      return [];
    }
    return parsed.segments.slice(0, -1);
  }

  static getOneLevelUpPath(url: string, tab: string = 'content'): string {
    const parsed = this.parsePath(url);
    if (!parsed || parsed.segments.length <= 1) {
      return parsed?.currentEntity?.type === 'program' ? '/programs' : '/courses';
    }

    const parentSegments = parsed.segments.slice(0, -1);
    const parentPath = this.buildFullPath(parentSegments);
    const parentEntity = parentSegments[parentSegments.length - 1];

    return `${parentPath}/${tab}`;
  }

  static addEntityToPath(
    currentPath: string,
    entityType: 'program' | 'course' | 'card',
    entityUri: string,
    tab: string = 'content',
  ): string {
    const parsed = this.parsePath(currentPath);
    const parentSegments = parsed ? parsed.segments : [];
    const newPath = this.buildPathForEntity(entityType, entityUri, parentSegments);
    return `${newPath}/${tab}`;
  }

  static getProgramUri(url: string, level: number = 0): string | null {
    const segments = this.getAllSegments(url);
    const programSegments = segments.filter((s) => s.type === 'program');
    if (level >= 0 && level < programSegments.length) {
      return programSegments[programSegments.length - 1 - level]?.uri || null;
    }
    return null;
  }

  static getCourseUri(url: string, level: number = 0): string | null {
    const segments = this.getAllSegments(url);
    const courseSegments = segments.filter((s) => s.type === 'course');
    if (level >= 0 && level < courseSegments.length) {
      return courseSegments[courseSegments.length - 1 - level]?.uri || null;
    }
    return null;
  }

  static getCurrentProgramUri(url: string): string | null {
    const parsed = this.parsePath(url);
    if (parsed?.currentEntity?.type === 'program') {
      return parsed.currentEntity.uri;
    }
    return this.getProgramUri(url, 0);
  }

  static getCurrentCourseUri(url: string): string | null {
    const parsed = this.parsePath(url);
    if (parsed?.currentEntity?.type === 'course') {
      return parsed.currentEntity.uri;
    }
    return this.getCourseUri(url, 0);
  }

  static getBackUrl(currentUrl: string): string {
    const cleanUrl = currentUrl.split('?')[0];
    const segments = this.getAllSegments(cleanUrl);

    if (segments.length === 0) {
      return '/';
    }

    if (segments.length === 1) {
      const firstSegment = segments[0];
      if (firstSegment.type === 'program') {
        return '/programs';
      } else if (firstSegment.type === 'course') {
        return '/courses';
      }
      return '/';
    }

    const parentSegments = segments.slice(0, -1);
    const parentPath = this.buildFullPath(parentSegments);
    return `${parentPath}/content`;
  }

  /**
   * Get the back URL for edit pages.
   * From edit page, back goes to the same entity's content page, not to parent list.
   * Example: /programs/xyz/edit -> /programs/xyz/content
   */
  static getBackUrlFromEdit(currentUrl: string): string {
    const cleanUrl = currentUrl.split('?')[0];

    // If URL ends with /edit, replace with /content
    if (cleanUrl.endsWith('/edit')) {
      return cleanUrl.replace(/\/edit$/, '/content');
    }

    // For other edit-related pages (collaborators, analytics, etc.), go to content
    const editTabs = ['/collaborators', '/analytics', '/participants', '/publish-settings'];
    for (const tab of editTabs) {
      if (cleanUrl.endsWith(tab)) {
        return cleanUrl.replace(new RegExp(`${tab}$`), '/content');
      }
    }

    // Fallback to regular getBackUrl
    return this.getBackUrl(currentUrl);
  }
}
